<script setup lang="ts">
/**
 * 「画布」页签：尺寸（viewBox）· 比例预设 · 背景色。
 *
 * 尺寸语义是**只动画板（viewBox），内容尺寸不变**（扩画布=留白）。
 * 背景色两分：**视图态**（只影响编辑器显示，不写文档）与**写入文档**（`<svg style="background-color">`，导出保留）。
 */
import { computed } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { RATIO_PRESETS } from '@/core/ops/canvas';
import { ui } from '@/stores/editor';
import ColorField from './fields/ColorField.vue';

const editor = useEditor();

const info = computed(() => editor.canvasInfo.value);
const size = computed(() => ({
  w: Math.round(info.value?.viewBox?.w ?? info.value?.width ?? 0),
  h: Math.round(info.value?.viewBox?.h ?? info.value?.height ?? 0),
}));
const docBg = computed(() => editor.docBackground.value);
const viewBg = computed(() => ui.canvasBg ?? docBg.value ?? '#ffffff');

const onSize = (e: Event): void => {
  const input = e.target as HTMLInputElement;
  const value = Number(input.value);
  if (!Number.isFinite(value) || value < 1) return;
  const isWidth = input.dataset.field === 'canvas-w';
  editor.setCanvasSize({
    width: isWidth ? value : size.value.w,
    height: isWidth ? size.value.h : value,
  });
};

const ratioActive = (ratio: number | null): boolean =>
  ratio !== null && info.value !== null && Math.abs(info.value.ratio - ratio) < 0.005;

const onViewBg = (color: string | null): void => {
  editor.setCanvasBackground(color, 'view');
};

const toggleDocBg = (): void => {
  editor.setCanvasBackground(docBg.value ? null : (ui.canvasBg ?? '#ffffff'), 'doc');
};
</script>

<template>
  <div class="sec">
    <div class="sec-head">
      <span>画布尺寸</span>
      <span class="grow" />
      <span class="panel-note">改尺寸=只改画板，内容不缩放</span>
    </div>
    <div class="field">
      <label>宽</label>
      <div class="input"><input data-field="canvas-w" type="number" min="1" :value="size.w" @change="onSize" /></div>
      <label>高</label>
      <div class="input"><input data-field="canvas-h" type="number" min="1" :value="size.h" @change="onSize" /></div>
    </div>
    <div class="seg">
      <button
        v-for="preset in RATIO_PRESETS"
        :key="preset.label"
        :class="{ 'is-active': preset.ratio === null ? false : ratioActive(preset.ratio) }"
        :title="preset.ratio === null ? '保持当前比例不变' : `按 ${preset.label} 调整宽度（保持高度，内容不缩放）`"
        @click="editor.setCanvasRatio(preset.ratio)"
      >
        {{ preset.label }}
      </button>
    </div>
  </div>

  <div class="sec">
    <div class="sec-head">
      <span>背景</span>
      <span class="grow" />
      <span class="chip">{{ docBg ?? '未写入文档' }}</span>
    </div>
    <ColorField :value="viewBg" allow-none @change="onViewBg" />
    <div class="field">
      <span class="panel-note">仅编辑器显示</span>
      <span class="grow" />
      <button class="ghost-btn" data-field="doc-bg" @click="toggleDocBg">
        {{ docBg ? '从文档中清除' : '写入文档' }}
      </button>
    </div>
    <p class="panel-note">写入文档后，别人打开这份 SVG 也会看到底色（导出保留）</p>
  </div>

  <div class="sec">
    <div class="sec-head"><span>文档</span></div>
    <div class="seg">
      <button data-field="new-doc" @click="editor.newDocument()">新建空文档</button>
      <button @click="editor.zoomFit()">适应窗口</button>
    </div>
  </div>
</template>
